import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Header } from '../../components/Header';
import { BottomNav } from '../../components/BottomNav';
import {
  Receipt, Check, AlertCircle, Search, User, UserPlus, X,
  Download, Ban, FileText, Users2, Mail, Printer, CreditCard,
} from 'lucide-react';
import { PRODUCTOS } from '../../constants/productos';
import { REGIMENES_FISCALES, USOS_CFDI, FORMAS_PAGO } from '../../constants/catalogosSat';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useNegocio } from '../../contexts/NegocioContext';
import { buildTicketText, sendTicketToBridge } from '../../utils/ticketPrint';
import { fmtMoney } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOMBRE_PRODUCTO = Object.fromEntries(PRODUCTOS.map(p => [p.clave, p.nombre]));
const FORMAS_PAGO_REALES = FORMAS_PAGO.filter(f => f.value !== '99');

const hoy = () => new Date().toISOString().slice(0, 10);
const MAX_DIAS_PERIODO_CLIENTE = 31;
// Debe coincidir con CLIENTE_ID_PUBLICO_GENERAL en backend/server.py
const CLIENTE_ID_PUBLICO_GENERAL = '__publico_general__';
const sumarDias = (fechaISO, dias) => {
  const d = new Date(`${fechaISO}T00:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
};

const RECEPTOR_VACIO = { rfc: '', razon_social: '', regimen_fiscal: '', uso_cfdi: '', codigo_postal: '', email: '' };
const up = (s) => (s || '').toUpperCase();

async function descargarArchivo(url, filename) {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'No se pudo descargar el archivo');
  }
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export function AdminFacturacion() {
  const [todosPuntos, setTodosPuntos] = useState([]);
  const [ambito, setAmbito] = useState('todos'); // 'todos' | 'punto' | 'cliente' — controla cuál filtro de abajo aplica
  const [puntoVentaId, setPuntoVentaId] = useState('');
  const [grupoAGestionar, setGrupoAGestionar] = useState(null); // hub (punto habilitado) cuyo grupo se está editando
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());

  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');

  const [pendientes, setPendientes] = useState([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [complementosPorFactura, setComplementosPorFactura] = useState({}); // { factura_id: [complementos] }

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [procesando, setProcesando] = useState(false);

  const [showIndividual, setShowIndividual] = useState(false);
  const [showGlobalConfirm, setShowGlobalConfirm] = useState(false);
  const [formaPagoGlobal, setFormaPagoGlobal] = useState('01');
  const [facturaEnviar, setFacturaEnviar] = useState(null);

  const [facturados, setFacturados] = useState([]);
  const [loadingFacturados, setLoadingFacturados] = useState(false);
  const [selectedFacturadosIds, setSelectedFacturadosIds] = useState(new Set());
  const [imprimiendo, setImprimiendo] = useState(false);

  const [abonosPendientes, setAbonosPendientes] = useState([]);
  const [loadingAbonos, setLoadingAbonos] = useState(false);
  const [abonoAFacturar, setAbonoAFacturar] = useState(null);

  const [resumenGrupos, setResumenGrupos] = useState([]);
  const [totalGeneralGrupos, setTotalGeneralGrupos] = useState(0);
  const { prefs } = usePreferences();
  const { nombreNegocio } = useNegocio();

  useEffect(() => { loadPuntosVenta(); loadClientes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadPendientes();
    loadHistorial();
    loadFacturados();
    loadAbonosPendientes();
    setSelectedIds(new Set());
    setSelectedFacturadosIds(new Set());
  }, [puntoVentaId, desde, hasta, clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dato de referencia: total facturado por grupo, independiente del filtro "Ver por" activo.
  useEffect(() => { loadResumenGrupos(); }, [desde, hasta]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al filtrar por cliente, el periodo se limita a un mes para mantener las consultas ágiles.
  useEffect(() => {
    if (clienteId && sumarDias(desde, MAX_DIAS_PERIODO_CLIENTE) < hasta) {
      setHasta(sumarDias(desde, MAX_DIAS_PERIODO_CLIENTE));
    }
  }, [clienteId, desde, hasta]);

  const loadPuntosVenta = async () => {
    try {
      const { data } = await axios.get(`${API}/puntos-venta`);
      setTodosPuntos(data || []);
    } catch {
      setError('Error al cargar puntos de venta');
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // La barra de navegación superior es sticky y tapa la parte de arriba de la pantalla;
    // sin este offset, scrollIntoView deja el título escondido detrás de ella.
    let offset = 16;
    const topNav = document.querySelector('.top-nav');
    if (topNav) {
      offset += (parseFloat(window.getComputedStyle(topNav).top) || 0) + topNav.offsetHeight;
    }
    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const ordenarPorNombre = (a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
  const puntosVenta = useMemo(
    () => todosPuntos.filter(p => p.facturacion_habilitada).slice().sort(ordenarPorNombre),
    [todosPuntos]
  );
  const puntosSinFacturacion = useMemo(
    () => todosPuntos.filter(p => !p.facturacion_habilitada).slice().sort(ordenarPorNombre),
    [todosPuntos]
  );
  const nombrePunto = (id) => up(todosPuntos.find(p => p.id === id)?.name);

  const loadClientes = async () => {
    try {
      // El catálogo de /api/clientes/{punto_venta_id} es global (no filtra por sucursal);
      // el segmento se ignora en el backend, así que no depende de tener un punto seleccionado.
      const { data } = await axios.get(`${API}/clientes/global`);
      setClientes((data || []).slice().sort((a, b) => a.nombre.localeCompare(b.nombre)));
    } catch {
      setClientes([]);
    }
  };

  const loadPendientes = async () => {
    setLoadingPendientes(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/facturacion/pendientes`, {
        params: { punto_venta_id: puntoVentaId, desde, hasta, cliente_id: clienteId || undefined },
      });
      setPendientes(data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar tickets pendientes');
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const loadHistorial = async () => {
    setLoadingHistorial(true);
    try {
      const { data } = await axios.get(`${API}/facturacion/historial`, {
        params: { punto_venta_id: puntoVentaId, desde, hasta, cliente_id: clienteId || undefined },
      });
      setHistorial(data || []);
    } catch {
      setHistorial([]);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const loadFacturados = async () => {
    setLoadingFacturados(true);
    try {
      const { data } = await axios.get(`${API}/facturacion/facturados`, {
        params: { punto_venta_id: puntoVentaId, desde, hasta, cliente_id: clienteId || undefined },
      });
      setFacturados(data || []);
    } catch {
      setFacturados([]);
    } finally {
      setLoadingFacturados(false);
    }
  };

  const loadAbonosPendientes = async () => {
    setLoadingAbonos(true);
    try {
      const { data } = await axios.get(`${API}/facturacion/abonos-pendientes`, {
        params: { punto_venta_id: puntoVentaId, desde, hasta, cliente_id: clienteId || undefined },
      });
      setAbonosPendientes(data || []);
    } catch {
      setAbonosPendientes([]);
    } finally {
      setLoadingAbonos(false);
    }
  };

  const loadResumenGrupos = async () => {
    try {
      const { data } = await axios.get(`${API}/facturacion/resumen-grupos`, { params: { desde, hasta } });
      setResumenGrupos(data?.grupos || []);
      setTotalGeneralGrupos(data?.total_general || 0);
    } catch {
      setResumenGrupos([]);
      setTotalGeneralGrupos(0);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendientes.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(pendientes.map(p => p.id)));
  };

  const seleccionados = useMemo(() => pendientes.filter(p => selectedIds.has(p.id)), [pendientes, selectedIds]);
  const subtotalSeleccionado = useMemo(() => seleccionados.reduce((sum, p) => sum + (p.total_venta || 0), 0), [seleccionados]);
  const hayCreditoSeleccionado = seleccionados.some(p => p.es_credito);

  const facturasEnPeriodo = useMemo(() => historial.filter(f => f.estatus !== 'cancelada'), [historial]);
  const totalFacturadoPeriodo = useMemo(() => facturasEnPeriodo.reduce((sum, f) => sum + (f.total || 0), 0), [facturasEnPeriodo]);

  const puntoVentaNombre = useMemo(() => puntosVenta.find(p => p.id === puntoVentaId)?.name || '', [puntosVenta, puntoVentaId]);
  const nombreCliente = useMemo(() => {
    const map = Object.fromEntries(clientes.map(c => [c.id, c.nombre]));
    return (clienteId) => clienteId ? (map[clienteId] || 'Cliente') : null;
  }, [clientes]);
  const miembrosGrupoActual = useMemo(
    () => puntosSinFacturacion.filter(p => p.grupo_facturacion_id === puntoVentaId),
    [puntosSinFacturacion, puntoVentaId]
  );

  const toggleSelectFacturado = (id) => {
    setSelectedFacturadosIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFacturados = () => {
    if (selectedFacturadosIds.size === facturados.length) setSelectedFacturadosIds(new Set());
    else setSelectedFacturadosIds(new Set(facturados.map(f => f.id)));
  };

  const handleImprimirTickets = async () => {
    if (!prefs.printerEnabled || !prefs.printerIp?.trim() || !prefs.bridgeUrl?.trim()) {
      setError('Configura y activa tu impresora térmica en Configuración antes de imprimir.');
      return;
    }
    const seleccionados = facturados.filter(f => selectedFacturadosIds.has(f.id));
    if (seleccionados.length === 0) return;
    setImprimiendo(true);
    setError('');
    let enviados = 0;
    for (const t of seleccionados) {
      const infoLines = [
        `Fecha: ${t.fecha}`,
        `Folio fiscal: ${t.folio_ticket || '—'}`,
        `Cliente: ${t.cliente_nombre}${t.cliente_rfc ? ` (${t.cliente_rfc})` : ''}`,
      ];
      const lines = buildTicketText({
        titulo: (nombreNegocio || 'TICKET').toUpperCase() + (puntoVentaNombre ? `\n   ${puntoVentaNombre}` : ''),
        infoLines,
        productos: t.productos,
        preciosSnapshot: t.precios_snapshot,
        pagoLabel: t.es_credito ? 'A crédito' : 'Efectivo',
        footerLines: [`Factura: ${t.factura_folio}`],
      });
      const ok = await sendTicketToBridge(prefs, lines);
      if (ok) enviados += 1;
    }
    setImprimiendo(false);
    if (enviados === seleccionados.length) {
      setSuccess(`${enviados} ticket(s) enviados a la impresora`);
      setTimeout(() => setSuccess(''), 4000);
    } else {
      setError(`Solo se enviaron ${enviados} de ${seleccionados.length} tickets. Revisa la conexión con la impresora.`);
    }
  };

  const resumenProductos = (productos) => {
    return Object.entries(productos || {})
      .filter(([, cant]) => cant > 0)
      .map(([clave, cant]) => `${cant} ${NOMBRE_PRODUCTO[clave] || clave}`)
      .join(', ');
  };

  const onFacturado = (mensaje) => {
    setSuccess(mensaje);
    setSelectedIds(new Set());
    setShowIndividual(false);
    setShowGlobalConfirm(false);
    // La factura se timbra con fecha de HOY, sin importar la fecha del ticket que se facturó;
    // si "Hasta" quedó en una fecha pasada, la factura recién creada no entraría en el historial.
    const hoyStr = hoy();
    if (hasta < hoyStr) {
      setHasta(hoyStr); // dispara el useEffect que recarga pendientes/historial con el nuevo rango
    } else {
      loadPendientes();
      loadHistorial();
    }
    setTimeout(() => setSuccess(''), 5000);
  };

  const onComplementoGenerado = (mensaje) => {
    setSuccess(mensaje);
    setAbonoAFacturar(null);
    setComplementosPorFactura({});
    loadAbonosPendientes();
    loadHistorial();
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleFacturarGlobal = async () => {
    setProcesando(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/facturacion/global`, {
        movimiento_ids: Array.from(selectedIds),
        forma_pago: formaPagoGlobal,
        desde,
        hasta,
      });
      onFacturado(`Factura global generada: folio ${data.serie}-${data.folio}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar la factura global');
    } finally {
      setProcesando(false);
    }
  };

  const handleDescargar = async (factura, formato) => {
    setError('');
    try {
      await descargarArchivo(`${API}/facturacion/${factura.id}/${formato}`, `factura_${factura.serie}-${factura.folio}.${formato}`);
    } catch (err) {
      setError(err.message || `No se pudo descargar el ${formato.toUpperCase()}`);
    }
  };

  const toggleComplementos = async (facturaId) => {
    if (complementosPorFactura[facturaId]) {
      setComplementosPorFactura(prev => { const next = { ...prev }; delete next[facturaId]; return next; });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/facturacion/${facturaId}/complementos`);
      setComplementosPorFactura(prev => ({ ...prev, [facturaId]: data }));
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudieron cargar los complementos de pago');
    }
  };

  const handleDescargarComplemento = async (complemento, formato) => {
    setError('');
    try {
      await descargarArchivo(`${API}/facturacion/complementos/${complemento.id}/${formato}`, `rep_${complemento.id}.${formato}`);
    } catch (err) {
      setError(err.message || `No se pudo descargar el ${formato.toUpperCase()}`);
    }
  };

  const handleCancelar = async (factura) => {
    if (!window.confirm(`¿Cancelar la factura ${factura.serie}-${factura.folio}? Los tickets quedarán disponibles para volver a facturarse.`)) return;
    setError('');
    try {
      await axios.post(`${API}/facturacion/${factura.id}/cancelar`, { motivo: '02' });
      loadPendientes();
      loadHistorial();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo cancelar la factura');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-facturacion-page">
      <Header title="Facturación" />

      <div className="page-container fade-in">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 shrink-0" />
            <p className="text-red-700 font-medium text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center gap-3">
            <Check className="w-6 h-6 text-green-600 shrink-0" />
            <p className="text-green-700 font-medium text-sm">{success}</p>
          </div>
        )}

        {puntosVenta.length === 0 ? (
          <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 text-center">
            <Receipt className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-600 font-medium mb-1">No hay puntos de venta habilitados para facturación</p>
            <p className="text-zinc-400 text-sm">Habilítalos desde Puntos de Venta (requiere código postal).</p>
          </div>
        ) : (
          <>
            {/* Filtros, agrupados por orden de aplicación: 1) ámbito (a qué te limitas), 2) el valor de ese ámbito, 3) fechas dentro de él */}
            <div className="bg-white border-2 border-zinc-200 rounded-xl p-4 mb-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">1. Ver por</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'todos', label: 'Todo' },
                    { id: 'punto', label: 'Punto de venta' },
                    { id: 'cliente', label: 'Cliente' },
                  ].map(op => (
                    <button
                      key={op.id}
                      onClick={() => {
                        setAmbito(op.id);
                        setPuntoVentaId('');
                        setClienteId('');
                      }}
                      className={`h-11 rounded-xl text-sm font-medium border-2 ${
                        ambito === op.id ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 bg-white text-zinc-600'
                      }`}
                      data-testid={`ambito-${op.id}`}
                    >
                      {op.label}
                    </button>
                  ))}
                </div>
              </div>

              {ambito === 'punto' && (
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">2. Punto de venta</label>
                  <select
                    value={puntoVentaId}
                    onChange={e => setPuntoVentaId(e.target.value)}
                    className="w-full h-12 px-3 border-2 border-zinc-200 rounded-xl bg-white text-sm"
                    data-testid="select-punto-facturacion"
                  >
                    <option value="">Todos los puntos de venta</option>
                    {puntosVenta.map(p => <option key={p.id} value={p.id}>{up(p.name)}</option>)}
                  </select>
                  {miembrosGrupoActual.length > 0 && (
                    <p className="text-xs text-zinc-400 mt-1">
                      Incluye {miembrosGrupoActual.length} sucursal(es) agrupada(s): {miembrosGrupoActual.map(p => up(p.name)).join(', ')}
                    </p>
                  )}
                </div>
              )}

              {ambito === 'cliente' && (
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">2. Cliente</label>
                  <select
                    value={clienteId}
                    onChange={e => setClienteId(e.target.value)}
                    className="w-full h-12 px-3 border-2 border-zinc-200 rounded-xl bg-white text-sm"
                    data-testid="select-cliente-facturacion"
                  >
                    <option value="">Todos los clientes</option>
                    <option value={CLIENTE_ID_PUBLICO_GENERAL}>Público en General (sin cliente)</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  {ambito === 'todos' ? '2. Rango de fechas' : '3. Rango de fechas'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">Desde</label>
                    <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
                      className="w-full h-12 px-3 border-2 border-zinc-200 rounded-xl text-sm" data-testid="input-desde" />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">Hasta</label>
                    <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
                      className="w-full h-12 px-3 border-2 border-zinc-200 rounded-xl text-sm" data-testid="input-hasta" />
                  </div>
                </div>
                {ambito === 'cliente' && (
                  <p className="text-xs text-zinc-400 mt-1">Al filtrar por cliente, el periodo se limita a {MAX_DIAS_PERIODO_CLIENTE} días.</p>
                )}
              </div>
            </div>

            {/* Accesos rápidos a las secciones de esta misma vista */}
            <div className="flex gap-2 overflow-x-auto mb-4 pb-1">
              <button
                onClick={() => scrollToSection('grupos-section')}
                className="shrink-0 px-3 h-9 rounded-full bg-zinc-100 text-zinc-700 text-xs font-medium whitespace-nowrap"
              >
                Grupos
              </button>
              <button
                onClick={() => scrollToSection('tickets-pendientes-section')}
                className="shrink-0 px-3 h-9 rounded-full bg-zinc-100 text-zinc-700 text-xs font-medium whitespace-nowrap"
              >
                Tickets pendientes ({pendientes.length})
              </button>
              <button
                onClick={() => scrollToSection('abonos-pendientes-section')}
                className="shrink-0 px-3 h-9 rounded-full bg-zinc-100 text-zinc-700 text-xs font-medium whitespace-nowrap"
              >
                Abonos pendientes ({abonosPendientes.length})
              </button>
              <button
                onClick={() => scrollToSection('historial-section')}
                className="shrink-0 px-3 h-9 rounded-full bg-zinc-100 text-zinc-700 text-xs font-medium whitespace-nowrap"
              >
                Historial de facturas
              </button>
              <button
                onClick={() => scrollToSection('facturados-section')}
                className="shrink-0 px-3 h-9 rounded-full bg-zinc-100 text-zinc-700 text-xs font-medium whitespace-nowrap"
              >
                Tickets facturados ({facturados.length})
              </button>
            </div>

            {/* Grupos de facturación: sucursales sin facturación propia agrupadas bajo una que sí factura */}
            <div id="grupos-section" className="bg-white border-2 border-zinc-200 rounded-xl p-4 mb-4">
              <h3 className="text-sm font-bold text-zinc-900 mb-1 flex items-center gap-2">
                <Users2 className="w-4 h-4 text-zinc-400" />
                Grupos de facturación
              </h3>
              <p className="text-xs text-zinc-500 mb-3">
                Agrupa sucursales sin facturación propia bajo una que sí factura, para ver y facturar sus ventas juntas.
              </p>

              {/* Total de referencia: suma de todos los grupos en el periodo, sin importar el filtro "Ver por" activo */}
              <div className="flex items-center justify-between px-3 py-2 bg-zinc-900 rounded-lg mb-3">
                <span className="text-xs font-medium text-zinc-300">Facturado en el periodo (todos los grupos)</span>
                <span className="text-sm font-bold text-white">{fmtMoney(totalGeneralGrupos)}</span>
              </div>

              <div className="space-y-2">
                {puntosVenta.map(hub => {
                  const miembros = puntosSinFacturacion.filter(p => p.grupo_facturacion_id === hub.id);
                  const totalHub = resumenGrupos.find(g => g.punto_venta_id === hub.id)?.total || 0;
                  return (
                    <div key={hub.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-zinc-50 rounded-lg">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">{up(hub.name)}</p>
                        <p className="text-xs text-zinc-400 truncate">
                          {miembros.length > 0 ? miembros.map(m => up(m.name)).join(', ') : 'Sin sucursales agrupadas'}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-zinc-800">{fmtMoney(totalHub)}</p>
                        {puntosSinFacturacion.length > 0 && (
                          <button
                            onClick={() => setGrupoAGestionar(hub)}
                            className="text-xs font-medium text-blue-600"
                          >
                            Gestionar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Total facturado en el periodo */}
            <div className="bg-white border-2 border-zinc-200 rounded-xl p-4 mb-4 flex items-center justify-between" data-testid="total-facturado-periodo">
              <div>
                <p className="text-xs font-medium text-zinc-500">Facturado en el periodo</p>
                <p className="text-2xl font-bold text-zinc-900">{fmtMoney(totalFacturadoPeriodo)}</p>
              </div>
              <p className="text-sm text-zinc-400">{facturasEnPeriodo.length} factura(s)</p>
            </div>

            {/* Lista de pendientes */}
            <div id="tickets-pendientes-section" className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-zinc-900">Tickets pendientes ({pendientes.length})</h3>
              {pendientes.length > 0 && (
                <button onClick={toggleSelectAll} className="text-sm font-medium text-blue-600">
                  {selectedIds.size === pendientes.length ? 'Ninguno' : 'Todos'}
                </button>
              )}
            </div>

            {loadingPendientes ? (
              <div className="flex justify-center py-8"><div className="spinner" /></div>
            ) : pendientes.length === 0 ? (
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 text-center text-zinc-400 text-sm mb-4">
                No hay tickets pendientes de facturar en este rango de fechas.
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {pendientes.map(p => (
                  <label
                    key={p.id}
                    className={`flex items-start gap-3 bg-white border-2 rounded-xl p-3 cursor-pointer ${selectedIds.has(p.id) ? 'border-blue-500 bg-blue-50/40' : 'border-zinc-200'}`}
                    data-testid={`ticket-${p.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="w-5 h-5 mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-zinc-800">{p.fecha}</span>
                        <span className="font-bold text-zinc-900">{fmtMoney(p.total_venta)}</span>
                      </div>
                      <p className="text-xs text-zinc-500 truncate">{resumenProductos(p.productos)}</p>
                      {nombreCliente(p.cliente_id) && (
                        <p className="text-xs text-zinc-600 font-medium truncate flex items-center gap-1 mt-0.5">
                          <User className="w-3 h-3 text-zinc-400 shrink-0" />
                          {nombreCliente(p.cliente_id)}
                        </p>
                      )}
                      {p.es_credito && (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Crédito</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Barra de acciones */}
            {selectedIds.size > 0 && (
              <div className="sticky bottom-16 bg-white border-2 border-zinc-200 rounded-xl p-4 mb-6 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-zinc-600">{selectedIds.size} seleccionado(s)</span>
                  <span className="font-bold text-lg text-zinc-900">{fmtMoney(subtotalSeleccionado)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowIndividual(true)}
                    className="h-12 bg-blue-600 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2"
                    data-testid="btn-facturar-individual"
                  >
                    <User className="w-4 h-4" />Individual
                  </button>
                  <button
                    onClick={() => setShowGlobalConfirm(true)}
                    disabled={hayCreditoSeleccionado}
                    className="h-12 bg-zinc-900 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2 disabled:opacity-40"
                    data-testid="btn-facturar-global"
                    title={hayCreditoSeleccionado ? 'No se pueden incluir ventas a crédito en la factura global' : ''}
                  >
                    <Users2 className="w-4 h-4" />Global
                  </button>
                </div>
                {hayCreditoSeleccionado && (
                  <p className="text-xs text-amber-600 mt-2">Hay ventas a crédito seleccionadas: solo pueden facturarse de forma individual.</p>
                )}
              </div>
            )}

            {/* Abonos pendientes de facturar */}
            <h3 id="abonos-pendientes-section" className="text-lg font-bold text-zinc-900 mb-3 mt-6 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-zinc-400" />
              Abonos pendientes de facturar ({abonosPendientes.length})
            </h3>
            {loadingAbonos ? (
              <div className="flex justify-center py-6"><div className="spinner" /></div>
            ) : abonosPendientes.length === 0 ? (
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 text-center text-zinc-400 text-sm">
                No hay abonos sin facturar en estos filtros.
              </div>
            ) : (
              <div className="space-y-2">
                {abonosPendientes.map(a => (
                  <div key={a.id} className="bg-white border-2 border-zinc-200 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-800 truncate">{a.cliente_nombre}</p>
                      <p className="text-xs text-zinc-400">
                        {(a.fecha || '').slice(0, 10)} · {a.user_nombre}
                        {a.monto_aplicado > 0 && ` · ${fmtMoney(a.monto_aplicado)} ya facturado`}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-zinc-900">{fmtMoney(a.saldo_disponible)}</p>
                      <button
                        onClick={() => setAbonoAFacturar(a)}
                        className="mt-1 text-xs font-medium text-blue-600"
                      >
                        Generar complemento
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Historial */}
            <h3 id="historial-section" className="text-lg font-bold text-zinc-900 mb-3 mt-6">Historial de facturas</h3>
            {loadingHistorial ? (
              <div className="flex justify-center py-6"><div className="spinner" /></div>
            ) : historial.length === 0 ? (
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 text-center text-zinc-400 text-sm">
                No hay facturas para estos filtros.
              </div>
            ) : (
              <div className="space-y-2">
                {historial.map(f => (
                  <div key={f.id} className="bg-white border-2 border-zinc-200 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-zinc-400" />
                        <span className="font-semibold text-zinc-800 text-sm">{f.serie}-{f.folio}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.tipo === 'global' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {f.tipo === 'global' ? 'Global' : 'Individual'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.estatus === 'cancelada' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {f.estatus === 'cancelada' ? 'Cancelada' : 'Timbrada'}
                        </span>
                        {f.metodo_pago === 'PPD' && f.estatus !== 'cancelada' && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.estatus_pago === 'pagada' ? 'bg-green-100 text-green-700' : f.estatus_pago === 'parcial' ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'}`}>
                            {f.estatus_pago === 'pagada' ? 'PPD pagada' : `PPD: debe ${fmtMoney(f.saldo_pendiente)}`}
                          </span>
                        )}
                      </div>
                      <span className="font-bold text-zinc-900">{fmtMoney(f.total)}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mb-2">
                      {f.receptor?.razon_social || 'Público en General'} · {(f.created_at || '').slice(0, 10)}
                      {nombreCliente(f.cliente_id) && <span> · Cliente: {nombreCliente(f.cliente_id)}</span>}
                      {f.correo_enviado_a && <span className="text-zinc-400"> · Enviada a {f.correo_enviado_a}</span>}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => handleDescargar(f, 'pdf')} className="flex-1 h-9 border border-zinc-300 rounded-lg text-xs font-medium flex items-center justify-center gap-1">
                        <Download className="w-3.5 h-3.5" />PDF
                      </button>
                      <button onClick={() => handleDescargar(f, 'xml')} className="flex-1 h-9 border border-zinc-300 rounded-lg text-xs font-medium flex items-center justify-center gap-1">
                        <Download className="w-3.5 h-3.5" />XML
                      </button>
                      {f.estatus !== 'cancelada' && (
                        <button onClick={() => setFacturaEnviar(f)} className="flex-1 h-9 border border-zinc-300 rounded-lg text-xs font-medium flex items-center justify-center gap-1">
                          <Mail className="w-3.5 h-3.5" />Correo
                        </button>
                      )}
                      {f.estatus !== 'cancelada' && (
                        <button onClick={() => handleCancelar(f)} className="flex-1 h-9 border border-red-200 text-red-600 rounded-lg text-xs font-medium flex items-center justify-center gap-1">
                          <Ban className="w-3.5 h-3.5" />Cancelar
                        </button>
                      )}
                    </div>
                    {f.metodo_pago === 'PPD' && (
                      <div className="mt-2">
                        <button onClick={() => toggleComplementos(f.id)} className="text-xs font-medium text-blue-600">
                          {complementosPorFactura[f.id] ? 'Ocultar complementos de pago' : 'Ver complementos de pago'}
                        </button>
                        {complementosPorFactura[f.id] && (
                          complementosPorFactura[f.id].length === 0 ? (
                            <p className="text-xs text-zinc-400 mt-1">Sin abonos declarados todavía.</p>
                          ) : (
                            <div className="mt-2 space-y-1">
                              {complementosPorFactura[f.id].map(c => (
                                <div key={c.id} className="flex items-center justify-between bg-zinc-50 rounded-lg px-2 py-1.5 text-xs">
                                  <span className="text-zinc-600">{(c.fecha_pago || '').slice(0, 10)} · {fmtMoney(c.monto_aplicado)}</span>
                                  <div className="flex gap-2">
                                    <button onClick={() => handleDescargarComplemento(c, 'pdf')} className="text-blue-600 font-medium">PDF</button>
                                    <button onClick={() => handleDescargarComplemento(c, 'xml')} className="text-blue-600 font-medium">XML</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Tickets facturados: imprimir en térmica 80mm */}
            <div id="facturados-section" className="flex items-center justify-between mb-3 mt-6">
              <h3 className="text-lg font-bold text-zinc-900">Imprimir tickets facturados ({facturados.length})</h3>
              {facturados.length > 0 && (
                <button onClick={toggleSelectAllFacturados} className="text-sm font-medium text-blue-600">
                  {selectedFacturadosIds.size === facturados.length ? 'Ninguno' : 'Todos'}
                </button>
              )}
            </div>
            {!prefs.printerEnabled && (
              <p className="text-xs text-amber-600 mb-3">Activa tu impresora térmica en Configuración para poder imprimir desde aquí.</p>
            )}
            {loadingFacturados ? (
              <div className="flex justify-center py-6"><div className="spinner" /></div>
            ) : facturados.length === 0 ? (
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-6 text-center text-zinc-400 text-sm mb-4">
                No hay tickets facturados en este periodo para esta sucursal.
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {facturados.map(t => (
                  <label
                    key={t.id}
                    className={`flex items-start gap-3 bg-white border-2 rounded-xl p-3 cursor-pointer ${selectedFacturadosIds.has(t.id) ? 'border-blue-500 bg-blue-50/40' : 'border-zinc-200'}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFacturadosIds.has(t.id)}
                      onChange={() => toggleSelectFacturado(t.id)}
                      className="w-5 h-5 mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-zinc-800">{t.folio_ticket || '—'} · {t.fecha}</span>
                        <span className="font-bold text-zinc-900">{fmtMoney(t.total_venta)}</span>
                      </div>
                      <p className="text-xs text-zinc-500 truncate">{t.cliente_nombre} · {resumenProductos(t.productos)}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {selectedFacturadosIds.size > 0 && (
              <div className="sticky bottom-16 bg-white border-2 border-zinc-200 rounded-xl p-4 mb-6 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-zinc-600">{selectedFacturadosIds.size} ticket(s)</span>
                </div>
                <button
                  onClick={handleImprimirTickets}
                  disabled={imprimiendo || !prefs.printerEnabled}
                  className="w-full h-12 bg-zinc-900 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {imprimiendo ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Printer className="w-5 h-5" />}
                  Imprimir seleccionados
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal: factura individual */}
        {showIndividual && (
          <FacturaIndividualModal
            seleccionados={seleccionados}
            subtotal={subtotalSeleccionado}
            desde={desde}
            hasta={hasta}
            procesando={procesando}
            setProcesando={setProcesando}
            setError={setError}
            onClose={() => setShowIndividual(false)}
            onFacturado={onFacturado}
          />
        )}

        {/* Modal: confirmación factura global */}
        {showGlobalConfirm && (
          <div className="modal-overlay" onClick={() => setShowGlobalConfirm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Factura global</h2>
                <button onClick={() => setShowGlobalConfirm(false)} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-zinc-600 mb-4">
                Se generará <strong>una sola factura</strong> a "Público en General" con los {selectedIds.size} tickets seleccionados
                (subtotal {fmtMoney(subtotalSeleccionado)}). Esta acción no se puede deshacer, aunque la factura sí puede cancelarse después.
              </p>
              <div className="mb-4">
                <label className="block text-xs font-medium text-zinc-500 mb-1">Forma de pago</label>
                <select
                  value={formaPagoGlobal}
                  onChange={e => setFormaPagoGlobal(e.target.value)}
                  className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm bg-white"
                  data-testid="select-forma-pago-global"
                >
                  {FORMAS_PAGO.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setShowGlobalConfirm(false)} className="h-12 bg-zinc-100 rounded-xl font-bold btn-press">
                  Cancelar
                </button>
                <button
                  onClick={handleFacturarGlobal}
                  disabled={procesando}
                  className="h-12 bg-zinc-900 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2"
                  data-testid="btn-confirmar-global"
                >
                  {procesando ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                  Generar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: enviar factura por correo */}
        {facturaEnviar && (
          <EnviarCorreoModal
            factura={facturaEnviar}
            setError={setError}
            onClose={() => setFacturaEnviar(null)}
            onEnviado={(email) => {
              setSuccess(`Factura enviada a ${email}`);
              setFacturaEnviar(null);
              loadHistorial();
              setTimeout(() => setSuccess(''), 5000);
            }}
          />
        )}

        {/* Modal: generar complemento de pago a partir de un abono */}
        {abonoAFacturar && (
          <GenerarComplementoModal
            abono={abonoAFacturar}
            setError={setError}
            onClose={() => setAbonoAFacturar(null)}
            onGenerado={onComplementoGenerado}
          />
        )}

        {/* Modal: gestionar grupo de facturación de un hub */}
        {grupoAGestionar && (
          <GrupoFacturacionModal
            hub={grupoAGestionar}
            puntosSinFacturacion={puntosSinFacturacion}
            nombrePunto={nombrePunto}
            setError={setError}
            onClose={() => setGrupoAGestionar(null)}
            onCambio={() => { loadPuntosVenta(); }}
          />
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function FacturaIndividualModal({ seleccionados, subtotal, desde, hasta, procesando, setProcesando, setError, onClose, onFacturado }) {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [modoNuevo, setModoNuevo] = useState(false);
  const [receptor, setReceptor] = useState(RECEPTOR_VACIO);
  const [formaPago, setFormaPago] = useState(() => seleccionados.some(s => s.es_credito) ? '99' : '01');

  useEffect(() => {
    // El catálogo de /api/clientes/{punto_venta_id} es global (no filtra por sucursal);
    // el segmento se ignora en el backend, así que usamos un valor fijo para no depender
    // de que puntoVentaId venga seleccionado en los filtros de la pantalla.
    axios.get(`${API}/clientes/global`)
      .then(({ data }) => setClientes((data || []).slice().sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))))
      .catch(() => setClientes([]));
  }, []);

  const clientesFiltrados = clientes.filter(c => c.nombre.toLowerCase().includes(busqueda.toLowerCase()));

  const seleccionarCliente = (c) => {
    setClienteId(c.id);
    setModoNuevo(false);
    setReceptor({
      rfc: c.rfc || '', razon_social: c.razon_social || '', regimen_fiscal: c.regimen_fiscal || '',
      uso_cfdi: c.uso_cfdi || '', codigo_postal: c.codigo_postal || '', email: c.email || '',
    });
  };

  const camposCompletos = receptor.rfc && receptor.razon_social && receptor.regimen_fiscal && receptor.uso_cfdi && receptor.codigo_postal;

  const handleConfirmar = async () => {
    if (!camposCompletos) {
      setError('Completa los datos fiscales del receptor (RFC, razón social, régimen, uso CFDI y código postal)');
      return;
    }
    setProcesando(true);
    setError('');
    try {
      const body = { movimiento_ids: seleccionados.map(s => s.id), receptor, forma_pago: formaPago, desde, hasta };
      if (clienteId && !modoNuevo) body.cliente_id = clienteId;
      const { data } = await axios.post(`${API}/facturacion/individual`, body);
      onFacturado(`Factura generada: folio ${data.serie}-${data.folio}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar la factura');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Factura individual</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-zinc-500 mb-4">{seleccionados.length} ticket(s) · Subtotal {fmtMoney(subtotal)}</p>

        <div className="mb-4">
          <label className="block text-xs font-medium text-zinc-500 mb-1">Forma de pago</label>
          <select
            value={formaPago}
            onChange={e => setFormaPago(e.target.value)}
            className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm bg-white"
            data-testid="select-forma-pago-individual"
          >
            {FORMAS_PAGO.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        {!modoNuevo && (
          <>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar cliente..."
                className="w-full h-12 pl-9 pr-3 border-2 border-zinc-200 rounded-xl text-sm"
                data-testid="input-buscar-cliente-factura"
              />
            </div>
            <div className="max-h-56 overflow-y-auto space-y-1 mb-3 border border-zinc-100 rounded-xl p-1">
              {clientesFiltrados.length === 0 ? (
                <p className="px-3 py-3 text-sm text-zinc-400 text-center">
                  {clientes.length === 0 ? 'No hay clientes registrados' : 'Sin resultados'}
                </p>
              ) : (
                clientesFiltrados.map(c => (
                  <button
                    key={c.id}
                    onClick={() => seleccionarCliente(c)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${clienteId === c.id ? 'bg-blue-100 text-blue-800' : 'bg-zinc-50 text-zinc-700'}`}
                  >
                    <span>{c.nombre}</span>
                    {c.rfc ? <span className="text-xs text-zinc-400">{c.rfc}</span> : <span className="text-xs text-amber-500">Sin datos fiscales</span>}
                  </button>
                ))
              )}
            </div>
            <button
              onClick={() => { setModoNuevo(true); setClienteId(''); setReceptor(RECEPTOR_VACIO); }}
              className="w-full h-11 border-2 border-dashed border-zinc-300 rounded-xl text-sm font-medium text-zinc-600 flex items-center justify-center gap-2 mb-4"
              data-testid="btn-datos-nuevos"
            >
              <UserPlus className="w-4 h-4" />Usar datos fiscales sin guardar cliente
            </button>
          </>
        )}

        {(modoNuevo || clienteId) && (
          <div className="space-y-3 mb-4 pt-3 border-t border-zinc-100">
            {modoNuevo && (
              <button onClick={() => setModoNuevo(false)} className="text-xs text-blue-600 font-medium">← Volver a buscar cliente</button>
            )}
            <input type="text" value={receptor.rfc} onChange={e => setReceptor({ ...receptor, rfc: e.target.value.toUpperCase() })}
              placeholder="RFC *" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm font-mono" data-testid="input-receptor-rfc" />
            <input type="text" value={receptor.razon_social} onChange={e => setReceptor({ ...receptor, razon_social: e.target.value })}
              placeholder="Razón social / Nombre *" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm" data-testid="input-receptor-razon" />
            <select value={receptor.regimen_fiscal} onChange={e => setReceptor({ ...receptor, regimen_fiscal: e.target.value })}
              className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm bg-white" data-testid="input-receptor-regimen">
              <option value="">Régimen fiscal *</option>
              {REGIMENES_FISCALES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select value={receptor.uso_cfdi} onChange={e => setReceptor({ ...receptor, uso_cfdi: e.target.value })}
              className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm bg-white" data-testid="input-receptor-uso">
              <option value="">Uso de CFDI *</option>
              {USOS_CFDI.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
            <input type="text" value={receptor.codigo_postal} onChange={e => setReceptor({ ...receptor, codigo_postal: e.target.value.replace(/\D/g, '').slice(0, 5) })}
              placeholder="Código postal *" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm" data-testid="input-receptor-cp" />
            <input type="email" value={receptor.email} onChange={e => setReceptor({ ...receptor, email: e.target.value })}
              placeholder="Correo (opcional)" className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm" data-testid="input-receptor-email" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <button onClick={onClose} className="h-12 bg-zinc-100 rounded-xl font-bold btn-press">Cancelar</button>
          <button
            onClick={handleConfirmar}
            disabled={procesando || !camposCompletos}
            className="h-12 bg-blue-600 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="btn-confirmar-individual"
          >
            {procesando ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
            Facturar
          </button>
        </div>
      </div>
    </div>
  );
}

function EnviarCorreoModal({ factura, setError, onClose, onEnviado }) {
  const [email, setEmail] = useState(factura.receptor?.email || factura.correo_enviado_a || '');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!email) {
      setError('Captura el correo del destinatario');
      return;
    }
    setEnviando(true);
    setError('');
    try {
      await axios.post(`${API}/facturacion/${factura.id}/enviar-correo`, { email });
      onEnviado(email);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo enviar el correo');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Enviar factura por correo</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Factura {factura.serie}-{factura.folio} · Facturama enviará el PDF y el XML adjuntos a este correo.
        </p>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="correo@ejemplo.com"
          className="w-full h-12 px-3 border-2 border-zinc-200 rounded-xl text-sm mb-4"
          data-testid="input-enviar-correo-email"
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onClose} className="h-12 bg-zinc-100 rounded-xl font-bold btn-press">Cancelar</button>
          <button
            onClick={handleEnviar}
            disabled={enviando || !email}
            className="h-12 bg-blue-600 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="btn-confirmar-enviar-correo"
          >
            {enviando ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Mail className="w-5 h-5" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}

function GenerarComplementoModal({ abono, setError, onClose, onGenerado }) {
  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(true);
  const [aplicaciones, setAplicaciones] = useState({}); // { factura_id: "monto texto" }
  const [formaPago, setFormaPago] = useState('01');
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    axios.get(`${API}/clientes/${abono.cliente_id}/facturas-pendientes`)
      .then(({ data }) => setFacturas(data || []))
      .catch(() => setFacturas([]))
      .finally(() => setLoadingFacturas(false));
  }, [abono.cliente_id]);

  const totalAplicado = Object.values(aplicaciones).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const excedeDisponible = totalAplicado > abono.saldo_disponible + 0.01;

  const handleConfirmar = async () => {
    const aplicacionesArr = Object.entries(aplicaciones)
      .map(([factura_id, v]) => ({ factura_id, monto: parseFloat(v) || 0 }))
      .filter(a => a.monto > 0);
    if (aplicacionesArr.length === 0) {
      setError('Indica el monto a aplicar en al menos una factura');
      return;
    }
    if (excedeDisponible) {
      setError('Lo aplicado no puede superar el saldo disponible del abono');
      return;
    }
    setProcesando(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/facturacion/complementos`, {
        abono_id: abono.id,
        forma_pago: formaPago,
        aplicaciones: aplicacionesArr,
      });
      setResultado(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo generar el complemento de pago');
    } finally {
      setProcesando(false);
    }
  };

  if (resultado) {
    return (
      <div className="modal-overlay" onClick={() => onGenerado('Complemento de pago generado')}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-1">Complemento de Pago (REP) timbrado</h2>
            <p className="text-xs text-zinc-400 break-all mb-4">{resultado.uuid_fiscal_rep}</p>
            <div className="grid grid-cols-2 gap-3 w-full">
              <button
                onClick={() => descargarArchivo(`${API}/facturacion/complementos/${resultado.complemento_pago_id}/pdf`, `rep_${resultado.complemento_pago_id}.pdf`).catch(e => setError(e.message))}
                className="h-12 border-2 border-zinc-200 rounded-xl font-bold btn-press"
              >
                Descargar PDF
              </button>
              <button
                onClick={() => onGenerado('Complemento de pago generado')}
                className="h-12 bg-zinc-900 text-white rounded-xl font-bold btn-press"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Generar complemento de pago</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          {abono.cliente_nombre} · Abono disponible: <strong>{fmtMoney(abono.saldo_disponible)}</strong>
        </p>

        {loadingFacturas ? (
          <div className="flex justify-center py-6"><div className="spinner" /></div>
        ) : facturas.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-6">Este cliente no tiene facturas PPD con saldo pendiente.</p>
        ) : (
          <>
            <p className="text-xs font-medium text-zinc-500 tracking-wide mb-2">APLICAR A FACTURAS</p>
            <div className="border border-zinc-200 rounded-xl divide-y divide-zinc-100 mb-3">
              {facturas.map(f => (
                <div key={f.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{f.serie}-{f.folio}</p>
                    <p className="text-xs text-zinc-400">Saldo: {fmtMoney(f.saldo_pendiente)}</p>
                  </div>
                  <input
                    type="number"
                    value={aplicaciones[f.id] || ''}
                    onChange={e => setAplicaciones(prev => ({ ...prev, [f.id]: e.target.value }))}
                    placeholder="0.00"
                    inputMode="decimal"
                    className="w-24 h-10 px-2 border border-zinc-300 rounded-lg text-sm text-right"
                  />
                </div>
              ))}
            </div>
            {excedeDisponible && (
              <p className="text-xs text-red-600 mb-3">Lo aplicado ({fmtMoney(totalAplicado)}) supera el saldo disponible del abono.</p>
            )}
            <div className="mb-4">
              <label className="block text-xs font-medium text-zinc-500 mb-1">Forma de pago</label>
              <select
                value={formaPago}
                onChange={e => setFormaPago(e.target.value)}
                className="w-full h-11 px-3 border-2 border-zinc-200 rounded-xl text-sm bg-white"
              >
                {FORMAS_PAGO_REALES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={onClose} className="h-12 bg-zinc-100 rounded-xl font-bold btn-press">Cancelar</button>
              <button
                onClick={handleConfirmar}
                disabled={procesando || totalAplicado <= 0 || excedeDisponible}
                className="h-12 bg-blue-600 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {procesando ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                Generar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GrupoFacturacionModal({ hub, puntosSinFacturacion, nombrePunto, setError, onClose, onCambio }) {
  const [guardandoId, setGuardandoId] = useState(null);

  const toggleMiembro = async (punto) => {
    const enEsteGrupo = punto.grupo_facturacion_id === hub.id;
    setGuardandoId(punto.id);
    setError('');
    try {
      await axios.patch(`${API}/puntos-venta/${punto.id}/grupo-facturacion`, {
        grupo_facturacion_id: enEsteGrupo ? null : hub.id,
      });
      onCambio();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo actualizar el grupo');
    } finally {
      setGuardandoId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Grupo de {up(hub.name)}</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Marca las sucursales sin facturación propia que quieres ver y facturar junto con {up(hub.name)}.
        </p>

        <div className="border border-zinc-200 rounded-xl divide-y divide-zinc-100 mb-4 max-h-72 overflow-y-auto">
          {puntosSinFacturacion.map(p => {
            const enEsteGrupo = p.grupo_facturacion_id === hub.id;
            const enOtroGrupo = p.grupo_facturacion_id && !enEsteGrupo;
            return (
              <label key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate">{up(p.name)}</p>
                  {enOtroGrupo && (
                    <p className="text-xs text-amber-600">Actualmente en el grupo de {nombrePunto(p.grupo_facturacion_id)}</p>
                  )}
                </div>
                {guardandoId === p.id ? (
                  <div className="spinner w-4 h-4 shrink-0" />
                ) : (
                  <input
                    type="checkbox"
                    checked={enEsteGrupo}
                    onChange={() => toggleMiembro(p)}
                    className="w-5 h-5 shrink-0"
                  />
                )}
              </label>
            );
          })}
        </div>

        <button onClick={onClose} className="w-full h-12 bg-zinc-900 text-white rounded-xl font-bold btn-press">
          Listo
        </button>
      </div>
    </div>
  );
}
